import MarkdownIt from 'markdown-it';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useFeature, useLocalize, useStreamAgent } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { AICard, FlexGap, Paragraph, Spinner, Subheading } from '@treams/treams-ui';

import { DisclaimerText } from '../../conversations/evaluation-submission/components/StreamingEvaluationAnswerAIInsights';
import { FeatureRatingSection } from '../../feature-ratings/FeatureRatingSection';

const MINIMUM_NOTES_FOR_SUMMARY = 3;
const RATING_SAMPLE_RATE = 1 / 3;

export default function OneOnOnesSummary(props: { subjectID: string; noteCount?: number }) {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const { stream, text } = useStreamAgent();
	const [streaming, setStreaming] = useState(false);
	const [cache, setCache] = useState<Record<string, string>>({});
	const hasAIInsights = useFeature(graphql.FeatureName.ConversationAiInsights);
	const cached = props.subjectID in cache;
	const displayText = cached ? cache[props.subjectID] : text;

	const skip = !hasAIInsights || !props.noteCount || props.noteCount < MINIMUM_NOTES_FOR_SUMMARY;

	useEffect(() => {
		if (skip || cached) return;

		const controller = new AbortController();
		setStreaming(true);

		stream(
			'NOTE_SUMMARIES',
			{ subjectUserID: props.subjectID, locale: language.id },
			controller.signal,
		)
			.then((accumulated) => {
				if (controller.signal.aborted) return;
				setCache((c) => ({ ...c, [props.subjectID]: accumulated ?? '' }));
			})
			.finally(() => {
				if (controller.signal.aborted) return;
				setStreaming(false);
			});

		return () => controller.abort();
	}, [skip, cached, props.subjectID, language.id]);

	const md = useMemo(() => new MarkdownIt(), []);
	const html = useMemo(() => (displayText ? md.render(displayText) : ''), [md, displayText]);

	const loading = streaming && !displayText;
	const done = cached;

	if (skip) {
		return null;
	}

	return (
		<Box px={[3, 4, 5]} mb={3}>
			<Box maxWidth={800} marginX="auto">
				<AICard
					title={
						<FlexGap gap={8} alignItems="center">
							<Subheading>{t('oneOnOnes.summary.title')}</Subheading>
							{loading && <Spinner color="primaryVibrant" />}
						</FlexGap>
					}
					collapsed={!displayText && !done}
					maxHeight={100}
					expandLabel={t('common.showMore')}
					collapseLabel={t('common.showLess')}
					markdown
				>
					{html ? (
						<div dangerouslySetInnerHTML={{ __html: html }} />
					) : (
						done && (
							<Paragraph>
								<i>{t('oneOnOnes.summary.noBulletPoints')}</i>
							</Paragraph>
						)
					)}
				</AICard>
				<DisclaimerText style={{ marginLeft: 16 }}>
					<a href={t('ai.disclaimer.link.to')} target="_blank" rel="noopener noreferrer">
						{t('evaluation.submission.aiInsights.disclaimer')}
					</a>
				</DisclaimerText>
				{done && (
					<Box mt={3}>
						<FeatureRatingSection
							featureName={graphql.FeatureName.ConversationAiInsights}
							targetID={props.subjectID}
							targetType={graphql.FeatureRatingTargetType.NoteSummary}
							i18nPrefix="feature.ratings.ai.noteSummary"
							sampleRate={RATING_SAMPLE_RATE}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
}
